import { 
  Leaf, 
  BookOpen, 
  Sprout, 
  Flower2, 
  Palette, 
  Star,
  Calendar,
  Lightbulb,
  LucideIcon 
} from "lucide-react";

export interface CategoryLink {
  name: string;
  slug: string;
  /** Optional absolute href override (e.g. for buying-guide pages at /best-*-uk). When set, takes precedence over the default /category/{slug}?sub={link.slug} URL. */
  href?: string;
  /** Optional nested children rendered as an indented sub-group beneath this link in the mega-menu. */
  children?: CategoryLink[];
}

export interface CategorySection {
  title: string;
  links: CategoryLink[];
}

export interface CategoryConfigItem {
  title: string;
  description: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  heroGradient: string;
  sections: CategorySection[];
}

export const categoryConfig: Record<string, CategoryConfigItem> = {
  "seasonal-guide": {
    title: "Seasonal Guide",
    description: "Month-by-month gardening tasks and tips for every season of the year",
    icon: Calendar,
    iconBg: "bg-teal-100 dark:bg-teal-900/30",
    iconColor: "text-teal-600 dark:text-teal-400",
    heroGradient: "bg-linear-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20",
    sections: [
      {
        title: "Spring",
        links: [
          { name: "March", slug: "march" },
          { name: "April", slug: "april" },
          { name: "May", slug: "may" },
        ],
      },
      {
        title: "Summer",
        links: [
          { name: "June", slug: "june" },
          { name: "July", slug: "july" },
          { name: "August", slug: "august" },
        ],
      },
      {
        title: "Autumn",
        links: [
          { name: "September", slug: "september" },
          { name: "October", slug: "october" },
          { name: "November", slug: "november" },
        ],
      },
      {
        title: "Winter",
        links: [
          { name: "December", slug: "december" },
          { name: "January", slug: "january" },
          { name: "February", slug: "february" },
        ],
      },
    ],
  },
  plants: {
    title: "Plants",
    description: "Comprehensive guides to all types of garden plants, from perennials to houseplants",
    icon: Leaf,
    iconBg: "bg-green-100 dark:bg-green-900/30",
    iconColor: "text-green-600 dark:text-green-400",
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20",
    sections: [
      {
        title: "Plant Types",
        links: [
          { name: "Perennials", slug: "perennials" },
          { name: "Annuals", slug: "annuals" },
          { name: "Shrubs", slug: "shrubs" },
          { name: "Trees", slug: "trees" },
          { name: "Climbers", slug: "climbers" },
          { name: "Bulbs", slug: "bulbs" },
        ],
      },
      {
        title: "House Plants",
        links: [
          { name: "Indoor Plants", slug: "indoor-plants" },
          { name: "Succulents & Cacti", slug: "succulents" },
          { name: "Tropical Plants", slug: "tropical" },
          { name: "Air Plants", slug: "air-plants" },
        ],
      },
      {
        title: "Plant Finder",
        links: [
          { name: "Plants for Shade", slug: "shade-plants" },
          { name: "Plants for Containers", slug: "container-plants" },
          { name: "Drought-Tolerant", slug: "drought-tolerant" },
          { name: "Wildlife-Friendly", slug: "wildlife-plants" },
        ],
      },
    ],
  },
  "how-to": {
    title: "How To",
    description: "Practical gardening guides, tutorials, and step-by-step instructions",
    icon: BookOpen,
    iconBg: "bg-blue-100 dark:bg-blue-900/30",
    iconColor: "text-blue-600 dark:text-blue-400",
    heroGradient: "bg-linear-to-br from-blue-50 to-sky-50 dark:from-blue-950/20 dark:to-sky-950/20",
    sections: [
      {
        title: "Gardening Basics",
        links: [
          { name: "Planting Guides", slug: "planting" },
          { name: "Pruning & Training", slug: "pruning" },
          { name: "Propagation", slug: "propagation" },
          { name: "Soil & Composting", slug: "soil" },
        ],
      },
      {
        title: "Seasonal Tasks",
        links: [
          { name: "Spring Gardening", slug: "spring" },
          { name: "Summer Gardening", slug: "summer" },
          { name: "Autumn Gardening", slug: "autumn" },
          { name: "Winter Gardening", slug: "winter" },
        ],
      },
      {
        title: "Problem Solving",
        links: [
          { name: "Pest Control", slug: "pests" },
          { name: "Disease Prevention", slug: "diseases" },
          { name: "Weed Control", slug: "weeds" },
          { name: "Garden Troubleshooting", slug: "troubleshooting" },
        ],
      },
    ],
  },
  "grow-guides": {
    title: "Grow Guides",
    description: "Detailed guides for growing vegetables, fruits, and herbs successfully",
    icon: Sprout,
    iconBg: "bg-lime-100 dark:bg-lime-900/30",
    iconColor: "text-lime-600 dark:text-lime-400",
    heroGradient: "bg-linear-to-br from-lime-50 to-green-50 dark:from-lime-950/20 dark:to-green-950/20",
    sections: [
      {
        title: "Vegetables",
        links: [
          { name: "Tomatoes", slug: "tomatoes" },
          { name: "Potatoes", slug: "potatoes" },
          { name: "Carrots & Root Veg", slug: "root-vegetables" },
          { name: "Beans & Peas", slug: "legumes" },
          { name: "Salad Leaves", slug: "salad" },
        ],
      },
      {
        title: "Fruits",
        links: [
          { name: "Strawberries", slug: "strawberries" },
          { name: "Raspberries", slug: "raspberries" },
          { name: "Apples & Pears", slug: "apples-pears" },
          { name: "Citrus", slug: "citrus" },
        ],
      },
      {
        title: "Herbs",
        links: [
          { name: "Culinary Herbs", slug: "culinary-herbs" },
          { name: "Medicinal Herbs", slug: "medicinal-herbs" },
          { name: "Annual Herbs", slug: "annual-herbs" },
          { name: "Perennial Herbs", slug: "perennial-herbs" },
        ],
      },
    ],
  },
  flowers: {
    title: "Flowers",
    description: "Everything you need to know about growing beautiful flowers year-round",
    icon: Flower2,
    iconBg: "bg-pink-100 dark:bg-pink-900/30",
    iconColor: "text-pink-600 dark:text-pink-400",
    heroGradient: "bg-linear-to-br from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20",
    sections: [
      {
        title: "By Season",
        links: [
          { name: "Spring Flowers", slug: "spring-flowers" },
          { name: "Summer Flowers", slug: "summer-flowers" },
          { name: "Autumn Flowers", slug: "autumn-flowers" },
          { name: "Winter Flowers", slug: "winter-flowers" },
        ],
      },
      {
        title: "Popular Flowers",
        links: [
          { name: "Roses", slug: "roses" },
          { name: "Dahlias", slug: "dahlias" },
          { name: "Tulips", slug: "tulips" },
          { name: "Sunflowers", slug: "sunflowers" },
        ],
      },
      {
        title: "Flower Gardens",
        links: [
          
          { name: "Cottage Garden", slug: "cottage-garden" },
          { name: "Wildflower Meadow", slug: "wildflowers" },
          { name: "Pollinator Garden", slug: "pollinator-garden" },
        ],
      },
    ],
  },
  "garden-design": {
    title: "Garden Design",
    description: "Inspiring ideas and practical advice for designing your perfect garden",
    icon: Palette,
    iconBg: "bg-purple-100 dark:bg-purple-900/30",
    iconColor: "text-purple-600 dark:text-purple-400",
    heroGradient: "bg-linear-to-br from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20",
    sections: [
      {
        title: "Design Ideas",
        links: [
          { name: "Small Gardens", slug: "small-gardens" },
          { name: "Large Gardens", slug: "large-gardens" },
          { name: "Courtyards & Patios", slug: "patios" },
          { name: "Front Gardens", slug: "front-gardens" },
        ],
      },
      {
        title: "Garden Styles",
        links: [
          { name: "Cottage Garden", slug: "cottage-garden" },
          { name: "Modern Gardens", slug: "modern-gardens" },
          { name: "Traditional Gardens", slug: "traditional-gardens" },
          { name: "Japanese Gardens", slug: "japanese-gardens" },
          { name: "Mediterranean Style", slug: "mediterranean" },
        ],
      },
      {
        title: "Features",
        links: [
          { name: "Water Features", slug: "water-features" },
          { name: "Garden Paths", slug: "paths" },
          { name: "Raised Beds", slug: "raised-beds" },
          { name: "Garden Lighting", slug: "lighting" },
        ],
      },
    ],
  },
  advice: {
    title: "Advice",
    description: "Expert gardening advice on lawns, soil, ponds, greenhouses, and solving common problems",
    icon: Lightbulb,
    iconBg: "bg-orange-100 dark:bg-orange-900/30",
    iconColor: "text-orange-600 dark:text-orange-400",
    heroGradient: "bg-linear-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20",
    sections: [
      {
        title: "Garden Care",
        links: [
          { name: "Lawns", slug: "lawns" },
          { name: "Soil & Composting", slug: "soil-composting" },
          { name: "Beds & Borders", slug: "beds-borders" },
          { name: "Pots & Containers", slug: "pots-containers" },
          { name: "Feeding & Watering", slug: "feeding-watering" },
        ],
      },
      {
        title: "Garden Features",
        links: [
          { name: "Ponds", slug: "ponds" },
          { name: "Greenhouses", slug: "greenhouses-advice" },
          { name: "Shade Gardening", slug: "shade-gardening" },
          { name: "DIY Projects", slug: "diy-projects" },
          { name: "Fencing", slug: "fencing" },
        ],
      },
      {
        title: "Problem Solving",
        links: [
          { name: "Solve Problems", slug: "solve-problems" },
          { name: "Weeding", slug: "weeding" },
          { name: "Beginner Guides", slug: "beginner-guides" },
          { name: "Harvesting & Storing", slug: "harvesting-storing" },
        ],
      },
    ],
  },
  reviews: {
    title: "Reviews & Buying Guides",
    description: "Expert reviews and buying guides for garden tools and equipment",
    icon: Star,
    iconBg: "bg-amber-100 dark:bg-amber-900/30",
    iconColor: "text-amber-600 dark:text-amber-400",
    heroGradient: "bg-linear-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20",
    sections: [
      {
        title: "Power Tools",
        links: [
          { name: "Generators", slug: "generators", href: "/generators" },
          { name: "Pressure Washers", slug: "pressure-washers", href: "/pressure-washers" },
          { name: "Cement Mixers", slug: "cement-mixers", href: "/cement-mixers" },
          { name: "Air Compressors", slug: "air-compressors", href: "/air-compressors" },
          { name: "Compactor Plates", slug: "compactor-plates", href: "/compactor-plates" },
          { name: "Air Fans & Ventilators", slug: "air-fans-ventilators", href: "/air-fans-ventilators" },
          { name: "Impact Drivers", slug: "impact-drivers", href: "/impact-drivers" },
          { name: "Water Pumps", slug: "water-pumps", href: "/water-pumps" },
          { name: "Mini Dumpers", slug: "mini-dumpers", href: "/mini-dumpers" },
          { name: "Earth Augers", slug: "earth-augers", href: "/earth-augers" },
          { name: "Welders", slug: "welders", href: "/welders" },
          { name: "Cordless Drills & Drill Drivers", slug: "cordless-drills", href: "/cordless-drills" },
          { name: "Impact Wrenches", slug: "impact-wrenches", href: "/impact-wrenches" },
          { name: "Battery Powered Ratchets", slug: "battery-ratchets", href: "/battery-ratchets" },
          { name: "Mains Power Drills (Non SDS)", slug: "mains-power-drills", href: "/mains-power-drills" },
          { name: "Mains SDS / SDS+ / SDS MAX Drills", slug: "mains-sds-drills", href: "/mains-sds-drills" },
          { name: "Cordless SDS & SDS+ Drills", slug: "cordless-sds-drills", href: "/cordless-sds-drills" },
          { name: "Cordless Screwdrivers", slug: "cordless-screwdrivers", href: "/cordless-screwdrivers" },
          { name: "Magnetic Drilling Systems", slug: "magnetic-drills", href: "/magnetic-drills" },
          { name: "Mains Screwdrivers", slug: "mains-screwdrivers", href: "/mains-screwdrivers" },
          { name: "Twin Packs & Kits", slug: "twin-packs-kits", href: "/twin-packs-kits" },
          { name: "Right Angle Drills", slug: "right-angle-drills", href: "/right-angle-drills" },
          { name: "Demolition Hammers & Breakers", slug: "demolition-hammers", href: "/demolition-hammers" },
          { name: "Drill Stands & Accessories", slug: "drill-stands", href: "/drill-stands" },
          { name: "Oscillating Multi Tools", slug: "oscillating-multi-tools", href: "/oscillating-multi-tools" },
          { name: "Multi Tool Blades & Accessories", slug: "multi-tool-blades", href: "/multi-tool-blades" },
          { name: "Hand Held Circular Saws", slug: "circular-saws", href: "/circular-saws" },
          { name: "Circular Saw Blades", slug: "circular-saw-blades", href: "/circular-saw-blades" },
          { name: "Circular Saw Bushing Washers", slug: "circular-saw-bushing-washers", href: "/circular-saw-bushing-washers" },
          { name: "Cut Off Saws", slug: "cut-off-saws", href: "/cut-off-saws" },
          { name: "Reciprocating Saws", slug: "reciprocating-saws", href: "/reciprocating-saws" },
          { name: "Jigsaws", slug: "jigsaws", href: "/jigsaws" },
          { name: "Routers & Laminate Trimmers", slug: "routers-laminate-trimmers", href: "/routers-laminate-trimmers" },
          { name: "Router Jigs & Templates", slug: "router-jigs-templates", href: "/router-jigs-templates" },
          { name: "Router Bit Sets", slug: "router-bit-sets", href: "/router-bit-sets" },
          { name: "Router Bits - Individual", slug: "router-bits", href: "/router-bits" },
          { name: "Router Guide Bushes & Bearings", slug: "router-guide-bushes", href: "/router-guide-bushes" },
          { name: "Router Accessories & Parts", slug: "router-accessories", href: "/router-accessories" },
          { name: "Router Tables", slug: "router-tables", href: "/router-tables" },
          { name: "Angle Grinders", slug: "angle-grinders", href: "/angle-grinders" },
          { name: "Angle Grinder Accessories", slug: "angle-grinder-accessories", href: "/angle-grinder-accessories" },
          { name: "Angle Grinder Discs", slug: "angle-grinder-discs", href: "/angle-grinder-discs" },
          { name: "Power Planers", slug: "power-planers", href: "/power-planers" },
          { name: "Electric Planer Blades", slug: "electric-planer-blades", href: "/electric-planer-blades" },
          { name: "Planer Thicknessers", slug: "planer-thicknessers", href: "/planer-thicknessers" },
          { name: "Orbital Sanders", slug: "orbital-sanders", href: "/orbital-sanders" },
          { name: "Drywall Sanders", slug: "drywall-sanders", href: "/drywall-sanders" },
          { name: "Polishers", slug: "polishers", href: "/polishers" },
          { name: "Portable Belt Sanders", slug: "belt-sanders", href: "/belt-sanders" },
          { name: "Plunge Saws", slug: "plunge-saws", href: "/plunge-saws" },
          { name: "Saw Guides & Rails", slug: "saw-guide-rails", href: "/saw-guide-rails" },
          { name: "Mini Rotary Tools & Engravers", slug: "mini-rotary-tools", href: "/mini-rotary-tools" },
          { name: "Rotary Tool Accessories", slug: "rotary-tool-accessories", href: "/rotary-tool-accessories" },
          { name: "Nail Guns & Staple Guns", slug: "nail-staple-guns", href: "/nail-staple-guns" },
          { name: "Biscuit & Dowel Jointers", slug: "biscuit-dowel-jointers", href: "/biscuit-dowel-jointers" },
          { name: "Heat Guns", slug: "heat-guns", href: "/heat-guns" },
          { name: "Table Saws", slug: "table-saws", href: "/table-saws" },
          { name: "Glue Guns & Glue Sticks", slug: "glue-guns", href: "/glue-guns" },
          { name: "Power Tool Batteries & Chargers", slug: "power-tool-batteries", href: "/power-tool-batteries" },
          { name: "Autofeed Screwdrivers", slug: "autofeed-screwdrivers", href: "/autofeed-screwdrivers" },
          { name: "Compound Mitre Saws", slug: "compound-mitre-saws", href: "/compound-mitre-saws" },
          { name: "Concrete Grinders & Cutters", slug: "concrete-grinders", href: "/concrete-grinders" },
          { name: "Electric Die Grinders", slug: "electric-die-grinders", href: "/electric-die-grinders" },
          { name: "Metal Nibblers & Shears", slug: "metal-nibblers-shears", href: "/metal-nibblers-shears" },
          { name: "Wet/Dry Extractors", slug: "wet-dry-extractors", href: "/wet-dry-extractors" },
          { name: "Powered Cutters", slug: "powered-cutters", href: "/powered-cutters" },
          { name: "Site Radios & Speakers", slug: "site-radios", href: "/site-radios" },
          { name: "Wall Chasers", slug: "wall-chasers", href: "/wall-chasers" },
          { name: "Bevellers & Accessories", slug: "bevellers", href: "/bevellers" },
          { name: "Powered Caulking Guns", slug: "powered-caulking-guns", href: "/powered-caulking-guns" },
          { name: "Rebar Tying Tools", slug: "rebar-tying-tools", href: "/rebar-tying-tools" },
          { name: "Riveters", slug: "riveters", href: "/riveters" },
          { name: "Tile Cutters", slug: "tile-cutters", href: "/tile-cutters" },
        ],
      },
      {
        title: "Garden Machinery",
        links: [
          { name: "Lawn Mowers", slug: "lawn-mowers", href: "/lawn-mowers" },
          { name: "Chainsaws", slug: "chainsaws", href: "/chainsaws" },
          { name: "Leaf Blowers", slug: "leaf-blowers", href: "/leaf-blowers" },
          { name: "Hedge Trimmers", slug: "hedge-trimmers", href: "/hedge-trimmers" },
          { name: "Tool Chests", slug: "tool-chests", href: "/tool-chests" },
        { name: "Strimmers", slug: "strimmers", href: "/strimmers" },
          { name: "Garden Multi-Tools", slug: "garden-multi-tools", href: "/garden-multi-tools" },
          { name: "Tillers & Cultivators", slug: "tillers-cultivators", href: "/tillers-cultivators" },
          { name: "Scarifiers", slug: "scarifiers", href: "/scarifiers" },
          { name: "Wood Chippers", slug: "wood-chippers", href: "/wood-chippers" },
          { name: "Shredders", slug: "shredders", href: "/shredders" },
          { name: "Log Splitters", slug: "log-splitters", href: "/log-splitters" },
          { name: "Trenchers", slug: "trenchers", href: "/trenchers" },
          { name: "Stump Grinders", slug: "stump-grinders", href: "/stump-grinders" },
          { name: "Carts & Trailers", slug: "carts-trailers", href: "/carts-trailers" },
        ],
      },
      {
        title: "Heating & Cooling",
        links: [
          { name: "Patio Heaters", slug: "patio-heaters", href: "/patio-heaters" },
          { name: "Space Heaters", slug: "space-heaters", href: "/space-heaters" },
          { name: "Wood Burning Stoves", slug: "wood-burning-stoves", href: "/wood-burning-stoves" },
          { name: "Fire Pits", slug: "fire-pits", href: "/fire-pits" },
          { name: "Air Fans & Ventilators", slug: "air-fans-ventilators", href: "/air-fans-ventilators" },
          { name: "Air Conditioning & Coolers", slug: "air-conditioning-coolers", href: "/air-conditioning-coolers" },
        ],
      },
      {
        title: "Garden Living",
        links: [
          { name: "Gazebos & Marquees", slug: "gazebos-marquees", href: "/gazebos-marquees" },
          { name: "Garden Parasols", slug: "garden-parasols", href: "/garden-parasols" },
          { name: "Garden Storage", slug: "garden-storage", href: "/garden-storage" },
          { name: "Garden Sheds", slug: "garden-sheds", href: "/garden-sheds" },
          { name: "Swings & Hammocks", slug: "garden-swings-hammocks", href: "/garden-swings-hammocks" },
          { name: "Garden Decor", slug: "garden-decor", href: "/garden-decor" },
          { name: "Outdoor Rugs", slug: "rugs-outdoor", href: "/rugs-outdoor" },
        ],
      },
      {
        title: "BBQ & Outdoor Cooking",
        links: [
          { name: "BBQs, Pizza Ovens & Smokers", slug: "bbqs-outdoor-cooking", href: "/bbqs-outdoor-cooking" },
        ],
      },

      {
        title: "Watering & Irrigation",
        links: [
          { name: "Hoses & Reels", slug: "hoses-reels", href: "/hoses-reels" },
          { name: "Sprinklers & Irrigation", slug: "sprinklers-irrigation", href: "/sprinklers-irrigation" },
          { name: "Watering Cans", slug: "watering-cans", href: "/watering-cans" },
          { name: "Water Butts", slug: "water-butts", href: "/water-butts" },
          { name: "Water Pumps", slug: "water-pumps", href: "/water-pumps" },
          { name: "Planters & Pots", slug: "planters-pots", href: "/planters-pots" },
        ],
      },
      {
        title: "Plants & Growing",
        links: [
          { name: "Plants", slug: "plants", href: "/plants" },
          { name: "Flowers", slug: "flowers", href: "/flowers" },
          { name: "Vegetables", slug: "vegetables", href: "/vegetables" },
          { name: "Fruits", slug: "fruits", href: "/fruits" },
          { name: "Seeds", slug: "seeds", href: "/seeds" },
          { name: "Compost", slug: "compost", href: "/compost" },
          { name: "Greenhouses", slug: "greenhouses", href: "/greenhouses" },
        ],
      },
      {
        title: "Pets & Wildlife",
        links: [
          { name: "Pet Supplies", slug: "pet-supplies", href: "/pet-supplies" },
          { name: "Bird Care", slug: "bird-care", href: "/bird-care" },
        ],
      },
      {
        title: "Play & Leisure",
        links: [
          { name: "Trampolines", slug: "trampolines", href: "/trampolines" },
          { name: "Hot Tubs & Pools", slug: "hot-tubs-pools", href: "/hot-tubs-pools" },
          { name: "Kids Outdoor Play", slug: "kids-outdoor-play", href: "/kids-outdoor-play" },
        ],
      },
      {
        title: "Workshop & Safety",
        links: [
          { name: "Workbenches & Vices", slug: "workbenches-vices", href: "/workbenches-vices" },
          { name: "Ladders & Steps", slug: "ladders-steps", href: "/ladders-steps" },
          { name: "Tool Chests", slug: "tool-chests", href: "/tool-chests" },
          { name: "Automotive Tools", slug: "automotive-tools", href: "/automotive-tools" },
          { name: "Safety & Workwear", slug: "safety-workwear", href: "/safety-workwear" },
          { name: "Locks & Security", slug: "locks-security", href: "/locks-security" },
          { name: "Winches & Lifting", slug: "winches-lifting", href: "/winches-lifting" },
          { name: "Cleaning Supplies", slug: "cleaning-supplies", href: "/cleaning-supplies" },
        ],
      },
      {
        title: "Landscaping",
        links: [
          { name: "Fencing & Gates", slug: "fencing-gates", href: "/fencing-gates" },
          { name: "Decking", slug: "decking", href: "/decking" },
          { name: "Paving Slabs", slug: "paving-slabs", href: "/paving-slabs" },
        ],
      },
      {
        title: "Seasonal",
        links: [
          { name: "Christmas Decorations", slug: "christmas-decorations", href: "/christmas-decorations" },
        ],
      },
      {
        title: "Grow Your Own",
        links: [
          { name: "Seeds", slug: "seeds", href: "/seeds" },
          { name: "Vegetables", slug: "vegetables", href: "/vegetables" },
          { name: "Fruits", slug: "fruits", href: "/fruits" },
          { name: "Garden Plants", slug: "garden-plants", href: "/garden-plants" },
          { name: "Compost", slug: "compost", href: "/compost" },
        ],
      },
      {
        title: "Hand Tools",
        links: [
        { name: "Secateurs", slug: "secateurs", href: "/secateurs" },
          { name: "Spades & Forks", slug: "spades-forks", href: "/spades-forks" },
          { name: "Trowels", slug: "trowels", href: "/trowels" },
          { name: "Loppers", slug: "loppers", href: "/loppers" },
        ],
      },
      {
        title: "Garden Kit",
        links: [
          { name: "Compost Bins", slug: "compost-bins", href: "/compost" },
          { name: "Greenhouses", slug: "greenhouses", href: "/greenhouses" },
          { name: "Garden Hoses", slug: "hoses", href: "/hoses-reels" },
          { name: "Water Butts", slug: "water-butts", href: "/water-butts" },
          { name: "Wheelbarrows", slug: "wheelbarrows", href: "/carts-trailers" },
        ],
      },
      {
        title: "Outdoor Furniture",
        links: [
        { name: "Garden Chairs", slug: "garden-chairs", href: "/garden-chairs" },
          { name: "Rattan Furniture", slug: "rattan-furniture", href: "/rattan-furniture" },
          { name: "Patio Sets", slug: "patio-sets", href: "/patio-sets" },
          { name: "Fire Pits", slug: "fire-pits", href: "/fire-pits" },
          { name: "Hot Tubs", slug: "hot-tubs", href: "/hot-tubs-pools" },
          { name: "BBQ & Pizza Ovens", slug: "bbq-pizza-ovens", href: "/bbqs-outdoor-cooking" },
        ],
      },
      {
        title: "By Brand",
        links: [
          { name: "Hyundai Power Products", slug: "hyundai" },
          { name: "VonHaus", slug: "vonhaus" },
          { name: "Mountfield", slug: "mountfield" },
          { name: "Honda", slug: "honda" },
          { name: "Bosch", slug: "bosch" },
          { name: "Makita", slug: "makita" },
          { name: "Stihl", slug: "stihl" },
          { name: "Flymo", slug: "flymo" },
          { name: "Webb", slug: "webb" },
          { name: "Einhell", slug: "einhell" },
          { name: "LawnMaster", slug: "lawnmaster" },
          { name: "Altrad Belle", slug: "altrad-belle" },
          { name: "Clarke", slug: "clarke" },
          { name: "JCB Tools", slug: "jcb-tools" },
        ],
      },
    ],
  },
};
